/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import Add from '@material-ui/icons/Add';
import {Moment} from 'moment';
import {translate} from '@ion/core/src/locale/locale';
import {getCurrentDate} from '@ion/core/src/utils/DateUtil';
import GridClearFiltersButton from '@ion/dls/src/components/buttons/GridClearFiltersButton';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import RefreshGridButton from '@ion/dls/src/components/buttons/RefreshGridButton';
import TertiaryButton from '@ion/dls/src/components/buttons/TertiaryButton';
import Dropdown from '@ion/dls/src/components/menus/Dropdown';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import DatePicker from '@ion/dls/src/editors/datetimepicker/DatePicker';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';

interface DropdownOption {
  label: string;
  value: string;
}

type CanDraftDate = Date | Moment | string | null;

const EMPTY_DATE_PLACEHOLDER = '- - - -  - -  - -';

export interface CanRequestsToolbarProps {
  canEsnOptions: DropdownOption[];
  selectedCanEsn: string;
  appliedCanStartDate: string | null;
  appliedCanEndDate: string | null;
  disableCreateCanRequest?: boolean;
  disabledCreateCanRequestTooltip?: string;
  onCanEsnChange: (event: any) => void;
  onCanRequestsSearch: (draftCanStartDate: CanDraftDate, draftCanEndDate: CanDraftDate) => void;
  onClearCanRequestsFilters: () => void;
  onOfflineCanRequestsRefresh: () => void;
  onCreateCanRequest: () => void;
}

const useStyles = makeStyles((theme: DLSTheme) => ({
  canRequestsToolbar : {
    display                        : 'flex',
    alignItems                     : 'flex-end',
    justifyContent                 : 'space-between',
    gap                            : '16px',
    width                          : '100%',
    margin                         : '12px 0 16px',
    [theme.breakpoints.down('sm')] : {
      alignItems : 'stretch',
      flexWrap   : 'wrap'
    }
  },
  canRequestsFilters : {
    display                        : 'flex',
    alignItems                     : 'flex-end',
    gap                            : '10px',
    [theme.breakpoints.down('sm')] : {
      width    : '100%',
      flexWrap : 'wrap'
    }
  },
  canRequestsActions : {
    display                        : 'flex',
    alignItems                     : 'center',
    [theme.breakpoints.down('sm')] : {
      minWidth : '100%'
    }
  },
  canEsnDropdownStyle : {
    width                   : '200px',
    '& .MuiInputBase-input' : {
      color : dlsColor('grey2')
    },
    [theme.breakpoints.down('sm')] : {
      width : '100%'
    }
  },
  canDateDropdownStyle : {
    width                          : '200px',
    [theme.breakpoints.down('sm')] : {
      width : '100%'
    }
  },
  canRequestsSearchActions : {
    display    : 'flex',
    alignItems : 'center',
    gap        : '8px',
    flexShrink : 0
  },
  createCanRequestButton : {
    margin : 0
  },
  createCanRequestButtonWrapper : {
    display : 'inline-flex'
  }
}));

/**
 * CAN Requests toolbar UI for ESN/date filtering and request actions.
 *
 * ```typescript
 * <CanRequestsToolbar
 *   canEsnOptions={canEsnOptions}
 *   selectedCanEsn={selectedCanEsn}
 *   appliedCanStartDate={appliedCanStartDate}
 *   appliedCanEndDate={appliedCanEndDate}
 *   onCanEsnChange={onCanEsnChange}
 *   onCanRequestsSearch={onCanRequestsSearch}
 *   onOfflineCanRequestsRefresh={onOfflineCanRequestsRefresh}
 *   onCreateCanRequest={onCreateCanRequest}
 * />
 * ```
 *
 * @param canEsnOptions               Dropdown options for the ESN selector.
 * @param selectedCanEsn              Currently selected ESN value.
 * @param onCanEsnChange              ESN dropdown change handler.
 * @param onCanRequestsSearch         Invoked when Search is clicked with draft start/end date values.
 * @param onOfflineCanRequestsRefresh Refresh action callback for CAN requests.
 * @param onCreateCanRequest          Create CAN request action callback.
 */
const CanRequestsToolbar = ({
  canEsnOptions,
  selectedCanEsn,
  appliedCanStartDate,
  appliedCanEndDate,
  onCanEsnChange,
  onCanRequestsSearch,
  onClearCanRequestsFilters,
  onOfflineCanRequestsRefresh,
  onCreateCanRequest,
  disableCreateCanRequest = false,
  disabledCreateCanRequestTooltip = ''
}: CanRequestsToolbarProps) => {
  const classes                                   = useStyles();
  const [draftCanStartDate, setDraftCanStartDate] = useState<CanDraftDate>(null);
  const [draftCanEndDate, setDraftCanEndDate]     = useState<CanDraftDate>(null);
  const [datePickerResetKey, setDatePickerResetKey] = useState(0);

  const {
    grid = {},
    canRequestsGrid = {},
    startDate = '',
    endDate = '',
    searchButton = ''
  } = (translate('vehicle-manager:vehicleHistoryGrid', {returnObjects: true}) || {});
  
  const {esn = ''} = grid || {};
  const {createCanRequest = ''} = canRequestsGrid || {};

  const onSearchClick = useCallback(() => {
    onCanRequestsSearch(draftCanStartDate, draftCanEndDate);
  }, [
    onCanRequestsSearch,
    draftCanStartDate,
    draftCanEndDate
  ]);
  
  const onStartDateChange = useCallback((date: CanDraftDate) => {
    setDraftCanStartDate(date);
  }, []);

  const onEndDateChange = useCallback((date: CanDraftDate) => {
    setDraftCanEndDate(date);
  }, []);

  const onClearFiltersClick = useCallback(() => {
    setDraftCanStartDate(null);
    setDraftCanEndDate(null);
    setDatePickerResetKey((value) => value + 1);
    onClearCanRequestsFilters();
  }, [onClearCanRequestsFilters]);

  const isSearchDisabled      = !draftCanStartDate || !draftCanEndDate;
  const areDateFiltersPresent = Boolean(appliedCanStartDate || appliedCanEndDate);

  return (
    <div className={classes.canRequestsToolbar}>
      <div className={classes.canRequestsFilters}>
        <div className={classes.canEsnDropdownStyle}>
          <Dropdown options={canEsnOptions}
            value={selectedCanEsn}
            fieldLabel={esn}
            placeholder={translate('common:dropdown.placeholder')}
            onChange={onCanEsnChange} />
        </div>
        <div className={classes.canDateDropdownStyle}>
          <DatePicker closeOnClickOutside
            key={`can-start-date-${datePickerResetKey}`}
            useDatePickerTextField
            manualInput
            maxDate={new Date(getCurrentDate())}
            hideClearButton
            fieldLabel={startDate}
            placeholder={EMPTY_DATE_PLACEHOLDER}
            onChange={onStartDateChange} />
        </div>
        <div className={classes.canDateDropdownStyle}>
          <DatePicker closeOnClickOutside
            key={`can-end-date-${datePickerResetKey}`}
            useDatePickerTextField
            manualInput
            maxDate={new Date(getCurrentDate())}
            hideClearButton
            fieldLabel={endDate}
            placeholder={EMPTY_DATE_PLACEHOLDER}
            onChange={onEndDateChange} />
        </div>
        <div className={classes.canRequestsSearchActions}>
          <PrimaryButton onClick={onSearchClick}
            disabled={isSearchDisabled}>
            {searchButton}
          </PrimaryButton>
          <GridClearFiltersButton onClearFilters={onClearFiltersClick}
            areFiltersApplied={areDateFiltersPresent} />
        </div>
      </div>
      <div className={classes.canRequestsActions}>
        <RefreshGridButton onRefresh={onOfflineCanRequestsRefresh} />
        <Tooltip arrow
          placement='bottom'
          title={disableCreateCanRequest ? disabledCreateCanRequestTooltip : ''}>
          <span className={classes.createCanRequestButtonWrapper}>
            <TertiaryButton onClick={onCreateCanRequest}
              disabled={disableCreateCanRequest}
              className={classes.createCanRequestButton}
              startIcon={<Add />}>
              {createCanRequest}
            </TertiaryButton>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

export default CanRequestsToolbar;
